import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { useState } from 'react'

import type { GroupHealthLine } from '@/api/reports-types'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { ReportExportButtons } from './report-export-buttons'
import { formatRatio, resolveBranchLabel, today } from './constants'
import { BranchFilterField, LoanOfficerFilterField } from './filters'
import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'
import { useGroupHealth, useReportsBranchReach } from './queries'
import { ReportHeaderNotice } from './report-header-notice'
import { RestrictedPanel } from './restricted-panel'

const DEFAULT_PAGE_SIZE = 50
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

/**
 * Group standing and portfolio quality, as at a date.
 *
 * `healthOutcome`, `status` and `reason` are rendered exactly as the server sends them — the
 * contract does not publish a closed set for any of the three, so this screen does not guess at a
 * mapping. `qualifies` against `qualifyThreshold` is the one pair the table builds a badge from.
 */
export function GroupHealthSection() {
  const permissions = useReportsPermissions()
  const reach = useReportsBranchReach()

  const [asOf, setAsOf] = useState(today)
  const [branchId, setBranchId] = useState<string | null>(null)
  const [loanOfficerId, setLoanOfficerId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const query = useGroupHealth(
    { asOf, branchId, loanOfficerId, page, pageSize },
    permissions.canViewGroupHealth && asOf !== '',
  )

  if (!permissions.canViewGroupHealth) {
    return <RestrictedPanel what="Group health" permission={REPORTS_PERMISSIONS.groupHealthView} />
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    setPage(next.pageIndex + 1)
    setPageSize(next.pageSize)
  }

  const helper = dataTableColumns<GroupHealthLine>()
  const columns: DataTableColumn<GroupHealthLine>[] = helper.columns([
    helper.accessor('groupCode', {
      header: 'Group',
      enableSorting: false,
      cell: (context) => <span className="font-mono text-xs">{context.row.original.groupCode}</span>,
      meta: { noWrap: true, widthClass: 'w-28', card: 'lead' },
    }),
    helper.accessor('groupName', {
      header: 'Name',
      enableSorting: false,
      cell: (context) => (
        <span className="font-medium text-heading">{context.row.original.groupName}</span>
      ),
      meta: { card: 'title' },
    }),
    helper.accessor('branchId', {
      header: 'Branch',
      enableSorting: false,
      cell: (context) => (
        <span className="text-xs">{resolveBranchLabel(reach.branches, context.row.original.branchId)}</span>
      ),
      meta: { noWrap: true, widthClass: 'w-28' },
    }),
    helper.accessor('loanOfficerName', {
      header: 'Officer',
      enableSorting: false,
      cell: (context) => context.row.original.loanOfficerName ?? <span className="text-muted">Unassigned</span>,
      meta: { widthClass: 'w-32' },
    }),
    helper.accessor('activeMemberCount', {
      header: 'Members',
      enableSorting: false,
      cell: (context) => (
        <span className="numeric tabular-nums">{context.row.original.activeMemberCount}</span>
      ),
      meta: { align: 'right', widthClass: 'w-24', cardLabel: 'Members' },
    }),
    helper.accessor('healthScore', {
      header: 'Score',
      enableSorting: false,
      cell: (context) => {
        const row = context.row.original
        return row.healthScore === null ? (
          <span className="text-muted">—</span>
        ) : (
          <span className="numeric tabular-nums">{row.healthScore.toFixed(1)}</span>
        )
      },
      meta: { align: 'right', widthClass: 'w-24', cardLabel: 'Health score' },
    }),
    helper.accessor('healthOutcome', {
      header: 'Outcome',
      enableSorting: false,
      cell: (context) => <span className="text-xs">{context.row.original.healthOutcome}</span>,
      meta: { widthClass: 'w-36' },
    }),
    helper.accessor('qualifies', {
      header: 'Qualifies',
      enableSorting: false,
      cell: (context) => {
        const row = context.row.original
        return (
          <div className="flex flex-col gap-1">
            <Badge tone={row.qualifies ? 'success' : 'danger'}>
              {row.qualifies ? 'Qualifies' : 'Does not qualify'}
            </Badge>
            {row.isProvisional ? <Badge tone="warning">Provisional</Badge> : null}
          </div>
        )
      },
      meta: { widthClass: 'w-36', card: 'lead' },
    }),
    helper.accessor('portfolio', {
      header: 'Outstanding',
      enableSorting: false,
      cell: (context) => {
        const portfolio = context.row.original.portfolio
        return portfolio === null ? (
          <span className="text-xs text-muted">No portfolio data</span>
        ) : (
          <MoneyDisplay value={portfolio.portfolioOutstanding.minor} currency={portfolio.portfolioOutstanding.currency} symbol={false} />
        )
      },
      meta: { align: 'right', widthClass: 'w-32', cardLabel: 'Outstanding' },
    }),
    helper.display({
      id: 'parRatio',
      header: 'PAR ratio',
      cell: (context) => {
        const portfolio = context.row.original.portfolio
        return portfolio === null ? (
          <span className="text-xs text-muted">—</span>
        ) : (
          <span className="numeric tabular-nums">{formatRatio(portfolio.parRatio)}</span>
        )
      },
      meta: { align: 'right', widthClass: 'w-24', cardLabel: 'PAR ratio' },
    }),
  ])

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="grid gap-3 p-4 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="group-health-as-of">As of</Label>
            <Input
              id="group-health-as-of"
              type="date"
              value={asOf}
              onChange={(event) => setAsOf(event.target.value)}
            />
          </div>
          <BranchFilterField
            id="group-health-branch"
            branchId={branchId}
            onChange={(next) => {
              setBranchId(next)
              setLoanOfficerId(null)
              setPage(1)
            }}
          />
          <LoanOfficerFilterField
            id="group-health-officer"
            branchId={branchId}
            loanOfficerId={loanOfficerId}
            onChange={(next) => {
              setLoanOfficerId(next)
              setPage(1)
            }}
          />
        </CardContent>
      </Card>

      {query.data ? <ReportHeaderNotice header={query.data.header} /> : null}

      <ReportExportButtons
        query={{ report: 'GroupHealth', asOf, branchId, loanOfficerId, page, pageSize }}
      />

      {query.data && query.data.groupsWithoutPortfolioData > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Portfolio data is missing for some groups</CardTitle>
            <Badge tone="warning">{query.data.groupsWithoutPortfolioData} groups</Badge>
          </CardHeader>
          <CardContent className="p-4 text-xs text-body">
            Their loan book could not be read for this date — their rows carry &ldquo;No portfolio
            data&rdquo; below rather than a zero, which would be a different and wrong claim.
          </CardContent>
        </Card>
      ) : null}

      <DataTable
        columns={columns}
        data={query.data?.rows}
        rowCount={query.data?.paging.totalRows}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={onPaginationChange}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => row.groupId}
        emptyTitle="No groups match"
        emptyDescription="No group in this branch and officer scope has a health row for this date."
      />
    </div>
  )
}
